<template>
  <div class="commission-allocation-rules-page">
    <div class="page-header">
      <h2 class="page-title">订单服务费分配规则</h2>
      <button @click="showAddModal = true" class="add-btn">+ 添加规则</button>
    </div>

    <!-- 余下服务费归属提示 -->
    <div class="info-banner">
      <span class="info-icon">ℹ️</span>
      <span>提示：未被任何分配规则覆盖的「余下服务费」会在订单结算时自动归入 <b>总部（platform）</b> 角色，无需单独配置规则；平台示范规则可在列表中查看。</span>
    </div>

    <div class="search-bar">
      <input type="text" v-model="searchKeyword" placeholder="搜索规则名称" class="search-input" @keyup.enter="onSearch" />
      <select v-model="searchRoleId" class="search-select" @change="onSearch">
        <option value="">全部角色</option>
        <option v-for="r in roleOptions" :key="r.id" :value="r.id">{{ r.name }}</option>
      </select>
      <select v-model="searchStatus" class="search-select" @change="onSearch">
        <option value="">全部状态</option>
        <option :value="1">启用</option>
        <option :value="0">禁用</option>
      </select>
      <select v-model="searchItemType" class="search-select" @change="onSearch">
        <option value="">全部服务费单个项目</option>
        <option v-for="(label, val) in itemTypeMap" :key="val" :value="val">{{ label }}</option>
      </select>
      <button @click="onSearch" class="search-btn">搜索</button>
      <span class="total-count">共 {{ total }} 条</span>
    </div>

    <!-- 翻页 -->
    <div class="pagination" v-if="total > pageSize">
      <button :disabled="currentPage <= 1" @click="changePage(currentPage - 1)">上一页</button>
      <span class="page-info">第 {{ currentPage }} / {{ lastPage }} 页</span>
      <button :disabled="currentPage >= lastPage" @click="changePage(currentPage + 1)">下一页</button>
      <select v-model="pageSize" class="page-size-select" @change="loadRules">
        <option :value="10">10 条/页</option>
        <option :value="20">20 条/页</option>
        <option :value="50">50 条/页</option>
        <option :value="100">100 条/页</option>
      </select>
    </div>

    <table class="rules-table" v-if="groupedRules.length">
      <thead>
        <tr>
          <th>规则名称</th>
          <th>服务类型</th>
          <th>服务费单个项目</th>
          <th>角色类型</th>
          <th>计算方式</th>
          <th>基础金额/费率</th>
          <th>优先级</th>
          <th>有效期</th>
          <th>状态</th>
        </tr>
      </thead>
      <tbody v-for="group in groupedRules" :key="'g-' + group.itemType">
        <tr class="group-row">
          <td :colspan="10">
            <span class="group-title">📦 分配服务费单个项目：{{ getItemTypeText(group.itemType) }}</span>
            <span class="group-count">（{{ group.rules.length }} 个角色）</span>
            <button class="group-edit-btn" @click="editByItemType(group.itemType)">批量编辑</button>
            <button class="group-delete-btn" @click="batchDeleteByItemType(group)">批量删除</button>
          </td>
        </tr>
        <tr v-for="rule in group.rules" :key="'r-' + rule.id">
          <td>{{ rule.rule_name }}</td>
          <td>{{ getServiceTypeText(rule.service_type_code) }}</td>
          <td>{{ getItemTypeText(rule.commission_item_type) }}</td>
          <td>{{ rule.role ? rule.role.name : (roleMap[rule.role_id] || '未知') }}</td>
          <td>{{ getCalculationTypeText(rule.calculation_type) }}</td>
          <td>
            <span v-if="rule.calculation_type === 1">{{ (rule.fixed_amount / 100).toFixed(2) }}元</span>
            <span v-else>{{ (rule.allocation_rate / 100).toFixed(2) }}%</span>
          </td>
          <td>{{ rule.priority }}</td>
          <td>{{ formatDateRange(rule.effective_time, rule.expire_time) }}</td>
          <td>
            <span :class="['status-badge', rule.status === 1 ? 'status-enabled' : 'status-disabled']">
              {{ rule.status === 1 ? '启用' : '禁用' }}
            </span>
          </td>
        </tr>
      </tbody>
    </table>

    <div v-if="rules.length === 0" class="empty-state">
      <span class="empty-icon">📭</span>
      <p>暂无分配规则</p>
      <button @click="showAddModal = true" class="empty-add-btn">添加第一条规则</button>
    </div>

    <!-- 添加/编辑弹窗 -->
    <div v-if="showAddModal || showEditModal" class="modal-overlay" @click.self="closeModal">
      <div class="modal-content">
        <div class="modal-header">
          <h3>{{ showEditModal ? '编辑规则' : '添加规则' }}</h3>
          <button @click="closeModal" class="close-btn">×</button>
        </div>
        <div class="modal-body">
          <div class="form-item">
            <label class="form-label">规则名称 <span class="required">*</span></label>
            <input type="text" v-model="formData.rule_name" class="form-input" placeholder="请输入规则名称" />
          </div>
          <div class="form-item">
            <label class="form-label">服务类型 <span class="required">*</span></label>
            <select v-model="formData.service_type_code" class="form-select">
              <option value="ALL">全部</option>
              <option value="DELIVERY">代送物品</option>
              <option value="PURCHASE">代买物品</option>
              <option value="PICKUP">代取快递</option>
              <option value="OTHER">其他</option>
            </select>
          </div>
          <div class="form-item">
            <label class="form-label">服务费单个项目 <span class="required">*</span></label>
            <select v-model="formData.commission_item_type" class="form-select">
              <option v-for="(label, val) in itemTypeMap" :key="val" :value="Number(val)">{{ label }}</option>
            </select>
            <span class="form-hint">通用=作用于整个服务费池（兼容旧规则）；选择具体单项则仅对该单项分佣，可与通用规则使用不同比例</span>
          </div>
          <div class="form-item">
            <label class="form-label">分佣角色与比例 <span class="required">*</span></label>
            <div class="role-matrix-wrap">
              <table class="role-matrix">
                <thead>
                  <tr>
                    <th style="width: 30%">分佣角色</th>
                    <th style="width: 32%">计算方式</th>
                    <th>比例(%) / 固定金额(元)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in formData.roles" :key="row.role_id">
                    <td>{{ roleMap[row.role_id] || '未知' }}</td>
                    <td>
                      <select v-model.number="row.calculation_type" class="form-select">
                        <option :value="1">固定金额</option>
                        <option :value="2">基于总服务费%</option>
                        <option :value="3">基于跑手服务费%</option>
                        <option :value="4">基于服务费单个项目%</option>
                      </select>
                    </td>
                    <td>
                      <input
                        v-if="row.calculation_type === 1"
                        type="number"
                        v-model="row.fixed_amount"
                        class="form-input"
                        placeholder="固定金额(元)"
                      />
                      <input
                        v-else
                        type="number"
                        v-model="row.allocation_rate"
                        class="form-input"
                        placeholder="比例(%)"
                      />
                    </td>
                  </tr>
                  <tr v-if="formData.roles.length === 0">
                    <td colspan="3" class="role-matrix-empty">暂无启用的分佣角色，请先在角色管理中添加</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <span class="form-hint">每个服务费项会分配给下方所有角色；各角色的计算方式与比例/金额均可单独设置。非通用单项的默认计算方式为「基于服务费单个项目%」。</span>
          </div>
          <div class="form-item">
            <label class="form-label">条件类型</label>
            <select v-model="formData.condition_type" class="form-select">
              <option :value="0">无条件</option>
              <option :value="1">首单奖励</option>
              <option :value="2">月度达标奖励</option>
            </select>
          </div>
          <div class="form-item">
            <label class="form-label">生效时间</label>
            <input type="datetime-local" v-model="formData.effective_time" class="form-input" />
          </div>
          <div class="form-item">
            <label class="form-label">过期时间</label>
            <input type="datetime-local" v-model="formData.expire_time" class="form-input" />
          </div>
          <div class="form-item">
            <label class="form-label">优先级（数值越小优先级越高） <span class="required">*</span></label>
            <input type="number" v-model="formData.priority" class="form-input" placeholder="数值越小优先级越高" />
          </div>
          <div class="form-item">
            <label class="form-label">状态 <span class="required">*</span></label>
            <select v-model="formData.status" class="form-select">
              <option :value="1">启用</option>
              <option :value="0">禁用</option>
            </select>
          </div>
          <div class="form-item">
            <label class="form-label">描述</label>
            <textarea v-model="formData.description" class="form-textarea" placeholder="请输入规则描述" rows="3"></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeModal" class="cancel-btn">取消</button>
          <button @click="saveRule" class="confirm-btn">{{ showEditModal ? '保存修改' : '确认添加' }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getCommissionAllocationRules,
  batchSaveCommissionAllocationRules,
  deleteCommissionAllocationRule
} from '@/api/commissionAllocationRules'
import { getCommissionRoleOptions } from '@/api/commissionRoles'

export default {
  name: 'CommissionAllocationRules',
  data() {
    return {
      rules: [],
      roleOptions: [],
      roleMap: {},
      itemTypeMap: {
        0: '通用(全部服务费)',
        1: '起步服务费',
        2: '距离服务费',
        3: '搬运服务费',
        4: '楼层服务费',
        5: '特殊时段服务费',
        6: '特殊天气服务费',
        7: '加急加价服务费'
      },
      // 前后端统一使用 service_type_code 字符串（ALL/DELIVERY/...），无需整数互转
      serviceTypeTextByCode: {
        ALL: '全部',
        DELIVERY: '代送物品',
        PURCHASE: '代买物品',
        PICKUP: '代取快递',
        OTHER: '其他'
      },
      searchKeyword: '',
      searchRoleId: '',
      searchStatus: '',
      searchItemType: '',
      currentPage: 1,
      pageSize: 50,
      total: 0,
      lastPage: 1,
      showAddModal: false,
      showEditModal: false,
      editingId: null,
      editingItemType: null,
      formData: {
        rule_name: '',
        commission_item_type: 0,
        service_type_code: 'ALL',
        effective_time: '',
        expire_time: '',
        priority: 127,
        status: 1,
        description: '',
        // 条件类型：0无条件/1首单奖励/2月度达标奖励（添加时默认"无条件"）
        condition_type: 0,
        // 每个角色一行：{ role_id, calculation_type(1固定/2总服务费%/3跑手服务费%/4单项%), allocation_rate, fixed_amount }
        roles: []
      }
    }
  },
  computed: {
    // 按服务费单个项目分组展示（保留后端筛选后的顺序，单项类型聚合）
    groupedRules() {
      const groups = {}
      const order = []
      this.rules.forEach(rule => {
        const key = rule.commission_item_type
        if (!groups[key]) {
          groups[key] = { itemType: key, rules: [] }
          order.push(key)
        }
        groups[key].rules.push(rule)
      })
      return order.map(k => groups[k])
    }
  },
  async mounted() {
    await this.loadRoleOptions()
    this.initFormRoles()
    this.loadRules()
  },
  methods: {
    async loadRoleOptions() {
      try {
        const res = await getCommissionRoleOptions()
        const list = (res.data && res.data.roles) || res.roles || res || []
        this.roleOptions = list
        this.roleMap = {}
        list.forEach(r => {
          this.roleMap[r.id] = r.name
        })
      } catch (e) {
      }
    },
    // 根据角色列表初始化空矩阵（默认非通用单项使用"基于服务费单个项目%"）
    initFormRoles() {
      this.formData.roles = this.roleOptions.map(r => ({
        id: null,
        role_id: r.id,
        calculation_type: 4,
        allocation_rate: '',
        base_amount: '',
      }))
    },
    onSearch() {
      this.currentPage = 1
      this.loadRules()
    },
    async loadRules() {
      try {
        const params = {
          page: this.currentPage,
          per_page: this.pageSize,
        }
        if (this.searchKeyword) params.keyword = this.searchKeyword
        if (this.searchRoleId) params.role_id = this.searchRoleId
        if (this.searchStatus !== '') params.status = this.searchStatus
        if (this.searchItemType !== '') params.commission_item_type = this.searchItemType

        const res = await getCommissionAllocationRules(params)
        // 兼容分页对象（{ data: [...], total, last_page }）与纯数组两种返回
        const result = res.data || res
        if (Array.isArray(result)) {
          this.rules = result
          this.total = result.length
          this.lastPage = 1
        } else {
          this.rules = result.data || []
          this.total = result.total || (result.data ? result.data.length : 0)
          this.lastPage = result.last_page || 1
        }
      } catch (error) {
      }
    },
    changePage(page) {
      if (page < 1 || page > this.lastPage) return
      this.currentPage = page
      this.loadRules()
    },
    getServiceTypeText(code) {
      return this.serviceTypeTextByCode[code] || '未知'
    },
    getItemTypeText(type) {
      return this.itemTypeMap[type] || '未知'
    },
    getCalculationTypeText(type) {
      const map = { 1: '固定金额', 2: '基于总服务费百分比', 3: '基于跑手服务费百分比', 4: '基于服务费单个项目百分比' }
      return map[type] || '未知'
    },
    formatDateRange(start, end) {
      if (!start) return '永久生效'
      const startDate = new Date(start).toLocaleString('zh-CN')
      const endDate = end ? new Date(end).toLocaleString('zh-CN') : '永久'
      return `${startDate} ~ ${endDate}`
    },
    // 编辑：以「服务费单个项目」为维度，基于已加载的该单项规则回填矩阵（与表格同源，避免不一致）
    async editRule(rule, itemRules) {
      this.editingItemType = rule.commission_item_type != null ? rule.commission_item_type : 0
      this.formData.commission_item_type = this.editingItemType
      // 前后端统一 service_type_code 字符串，直接回填
      this.formData.service_type_code = rule.service_type_code || 'ALL'
      this.formData.priority = rule.priority
      this.formData.status = rule.status
      this.formData.effective_time = rule.effective_time ? new Date(rule.effective_time).toISOString().slice(0, 16) : ''
      this.formData.expire_time = rule.expire_time ? new Date(rule.expire_time).toISOString().slice(0, 16) : ''
      this.formData.description = rule.description || ''
      this.formData.rule_name = rule.rule_name || ''
      this.formData.condition_type = rule.condition_type != null ? rule.condition_type : 0

      const rules = itemRules && itemRules.length ? itemRules : [rule]
      const map = {}
      rules.forEach(r => { map[r.role_id] = r })
      // 用角色列表构建矩阵，已存在规则的回填计算方式与比例/金额（与表格第49行逐角色一致）
      this.formData.roles = this.roleOptions.map(r => {
        const exist = map[r.id]
        if (exist) {
          return {
            id: exist.id,
            role_id: r.id,
            calculation_type: exist.calculation_type,
            allocation_rate: exist.allocation_rate != null ? (exist.allocation_rate / 100).toFixed(2) : '',
            fixed_amount: exist.fixed_amount != null ? (exist.fixed_amount / 100).toFixed(2) : '',
          }
        }
        return { id: null, role_id: r.id, calculation_type: 4, allocation_rate: '', fixed_amount: '' }
      })

      this.showEditModal = true
    },
    // 分组标题处"批量编辑"：以该单项分组触发整体矩阵编辑
    editByItemType(itemType) {
      const group = this.groupedRules.find(g => g.itemType === itemType)
      if (group && group.rules.length) this.editRule(group.rules[0], group.rules)
    },
    // 分组标题处"批量删除"：删除该服务费单项下所有角色的规则
    async batchDeleteByItemType(group) {
      if (!confirm(`确定要删除「${this.getItemTypeText(group.itemType)}」下的全部 ${group.rules.length} 条规则吗？此操作不可恢复`)) return

      try {
        await Promise.all(group.rules.map(r => deleteCommissionAllocationRule(r.id)))
        alert('批量删除成功')
        this.loadRules()
      } catch (error) {
        alert('批量删除失败: ' + (error.response?.data?.message || ''))
      }
    },
    closeModal() {
      this.showAddModal = false
      this.showEditModal = false
      this.editingItemType = null
      this.formData = {
        rule_name: '',
        commission_item_type: 0,
        service_type_code: 'ALL',
        effective_time: '',
        expire_time: '',
        priority: 127,
        status: 1,
        description: '',
        condition_type: 0,
        roles: []
      }
      this.initFormRoles()
    },
    async saveRule() {
      if (!this.formData.commission_item_type && this.formData.commission_item_type !== 0) {
        alert('请选择服务费单个项目')
        return
      }
      if (!this.formData.roles || this.formData.roles.length === 0) {
        alert('暂无分佣角色，无法保存')
        return
      }

      // 校验每个角色：固定金额需填金额，百分比需填比例
      for (const row of this.formData.roles) {
        if (row.calculation_type === 1) {
          if (!row.fixed_amount && row.fixed_amount !== 0) {
            alert(`角色「${this.roleMap[row.role_id] || row.role_id}」请填写固定金额`)
            return
          }
        } else {
          if (!row.allocation_rate && row.allocation_rate !== 0) {
            alert(`角色「${this.roleMap[row.role_id] || row.role_id}」请填写分佣比例`)
            return
          }
        }
      }

      const roles = this.formData.roles.map(r => ({
        id: r.id != null ? Number(r.id) : null,
        role_id: r.role_id,
        calculation_type: Number(r.calculation_type),
        allocation_rate: r.calculation_type === 1 ? 0 : Math.round(parseFloat(r.allocation_rate || 0) * 100),
        fixed_amount: r.calculation_type === 1 ? Math.round(parseFloat(r.fixed_amount || 0) * 100) : 0,
      }))

      const data = {
        commission_item_type: Number(this.formData.commission_item_type),
        // 前后端统一 service_type_code 字符串，直接透传
        service_type_code: this.formData.service_type_code || 'ALL',
        rule_name: this.formData.rule_name || '',
        condition_type: Number(this.formData.condition_type || 0),
        priority: Number(this.formData.priority || 0),
        status: Number(this.formData.status),
        effective_time: this.formData.effective_time || null,
        expire_time: this.formData.expire_time || null,
        description: this.formData.description || '',
        roles,
      }

      try {
        await batchSaveCommissionAllocationRules(data)
        alert(this.editingItemType != null ? '修改成功' : '添加成功')
        this.closeModal()
        this.loadRules()
      } catch (error) {
        alert('保存失败: ' + (error.response?.data?.message || ''))
      }
    }
  }
}
</script>

<style scoped>
.commission-allocation-rules-page {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-title {
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.add-btn {
  padding: 10px 20px;
  background: #667eea;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.3s;
}

.add-btn:hover {
  background: #5a6fd6;
}

.info-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  padding: 10px 14px;
  background: #eef6ff;
  border: 1px solid #cfe3ff;
  border-radius: 6px;
  font-size: 13px;
  color: #2c5a8a;
  line-height: 1.5;
}

.info-banner .info-icon {
  font-size: 15px;
}

.search-bar {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.search-input {
  padding: 10px 14px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  min-width: 200px;
}

.search-select {
  padding: 10px 14px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  background: #fff;
}

.search-btn {
  padding: 10px 20px;
  background: #667eea;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
}

.total-count {
  align-self: center;
  color: #999;
  font-size: 13px;
  margin-left: auto;
}

.pagination {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 16px 0;
  flex-wrap: wrap;
}

.pagination button {
  padding: 7px 16px;
  background: #667eea;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
}

.pagination button:disabled {
  background: #c3c9e8;
  cursor: not-allowed;
}

.page-info {
  color: #555;
  font-size: 13px;
}

.page-size-select {
  padding: 7px 10px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 13px;
  background: #fff;
}

.rules-table {
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
}

.rules-table th,
.rules-table td {
  padding: 14px 16px;
  text-align: left;
  border-bottom: 1px solid #eee;
  font-size: 14px;
}

.rules-table th {
  background: #f8f9fa;
  font-weight: 600;
  color: #333;
}

.role-matrix-wrap {
  border: 1px solid #eee;
  border-radius: 8px;
  overflow: hidden;
}

.role-matrix {
  width: 100%;
  border-collapse: collapse;
  background: #fff;
}

.role-matrix th,
.role-matrix td {
  padding: 10px 12px;
  text-align: left;
  border-bottom: 1px solid #f0f0f0;
  font-size: 14px;
}

.role-matrix th {
  background: #f8f9fa;
  font-weight: 600;
  color: #333;
}

.role-matrix .form-select,
.role-matrix .form-input {
  width: 100%;
  padding: 7px 10px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 13px;
}

.role-matrix-empty {
  text-align: center;
  color: #999;
  padding: 18px 0;
}

.group-row td {
  background: #eef3fb;
  font-weight: 600;
  color: #2c3e50;
  padding: 10px 14px;
  border-bottom: 2px solid #d6e2f5;
}

.group-title {
  font-size: 14px;
}

.group-count {
  color: #7a8aa0;
  font-weight: 400;
  font-size: 12px;
  margin-left: 4px;
}

.group-edit-btn {
  float: right;
  padding: 4px 12px;
  font-size: 12px;
  color: #2c7be5;
  background: #fff;
  border: 1px solid #2c7be5;
  border-radius: 6px;
  cursor: pointer;
  margin-left: 8px;
}

.group-delete-btn {
  float: right;
  padding: 4px 12px;
  font-size: 12px;
  color: #e74c3c;
  background: #fff;
  border: 1px solid #e74c3c;
  border-radius: 6px;
  cursor: pointer;
  margin-left: 8px;
}

.group-delete-btn:hover {
  background: #e74c3c;
  color: #fff;
}

.group-edit-btn:hover {
  background: #2c7be5;
  color: #fff;
}

.rules-table tr:hover {
  background: #f8f9fa;
}

.status-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
}

.status-enabled {
  background: #d4edda;
  color: #155724;
}

.status-disabled {
  background: #f8d7da;
  color: #721c24;
}

.edit-btn,
.delete-btn {
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
  margin-right: 8px;
  transition: opacity 0.3s;
}

.edit-btn {
  background: #667eea;
  color: #fff;
}

.delete-btn {
  background: #e74c3c;
  color: #fff;
}

.edit-btn:hover,
.delete-btn:hover {
  opacity: 0.8;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
}

.empty-icon {
  font-size: 48px;
  display: block;
  margin-bottom: 16px;
}

.empty-state p {
  color: #999;
  margin: 0 0 20px 0;
}

.empty-add-btn {
  padding: 10px 24px;
  background: #667eea;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: #fff;
  border-radius: 8px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
}

.modal-header h3 {
  margin: 0;
  font-size: 16px;
  color: #333;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  color: #999;
  cursor: pointer;
  line-height: 1;
}

.close-btn:hover {
  color: #333;
}

.modal-body {
  padding: 20px;
}

.form-item {
  margin-bottom: 16px;
}

.form-label {
  display: block;
  font-size: 14px;
  color: #333;
  margin-bottom: 8px;
  font-weight: 500;
}

.required {
  color: #e74c3c;
}

.form-input,
.form-select,
.form-textarea {
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
  background: #fff;
}

.form-textarea {
  resize: vertical;
}

.form-input:focus,
.form-select:focus,
.form-textarea:focus {
  outline: none;
  border-color: #667eea;
}

.form-hint {
  display: block;
  font-size: 12px;
  color: #999;
  margin-top: 6px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 20px;
  border-top: 1px solid #eee;
}

.cancel-btn,
.confirm-btn {
  padding: 10px 24px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
}

.cancel-btn {
  background: #f0f0f0;
  color: #666;
}

.confirm-btn {
  background: #667eea;
  color: #fff;
}

.cancel-btn:hover {
  background: #e0e0e0;
}

.confirm-btn:hover {
  background: #5a6fd6;
}
</style>
